/**
 * OrgBudgetSettings — admin UI for per-org LLM monthly budgets.
 *
 * Lists every org, shows current period usage with progress bar, and lets
 * the admin edit the limit / hardCutoff. Saves invalidate the server-side
 * cache via the PUT endpoint so the new limit takes effect immediately.
 */

import React, { useEffect, useState, useCallback } from 'react';
import { getApiClient } from '@/components/admin/adminAuthClients';

interface OrgBudgetStatus {
  budget: {
    _id: string;
    organizationId: string;
    monthlyLimitUsd: number;
    hardCutoff: boolean;
    alertThresholds: number[];
    periodStart: string;
    periodEnd: string;
    notes?: string;
  } | null;
  status: {
    allowed: boolean;
    currentUsageUsd: number;
    limitUsd: number;
    remainingUsd: number;
    periodEndsAt: string | null;
    thresholdsCrossed: number[];
  };
}

interface OrganizationListItem {
  id: string;
  name: string;
}

function formatCost(cost: number): string {
  if (!Number.isFinite(cost)) return '—';
  if (cost === 0) return '$0.00';
  if (cost < 0.01) return `$${cost.toFixed(6)}`;
  return `$${cost.toFixed(2)}`;
}

function progressColor(pct: number): string {
  if (pct >= 1) return 'bg-red-500';
  if (pct >= 0.8) return 'bg-amber-500';
  return 'bg-emerald-500';
}

interface OrgRowState {
  org: OrganizationListItem;
  status: OrgBudgetStatus | null;
  loading: boolean;
}

export const OrgBudgetSettings: React.FC = () => {
  const [rows, setRows] = useState<OrgRowState[]>([]);
  const [loadingOrgs, setLoadingOrgs] = useState(false);
  const [editing, setEditing] = useState<string | null>(null);
  const [draft, setDraft] = useState<{
    monthlyLimitUsd: string;
    hardCutoff: boolean;
    notes: string;
  }>({ monthlyLimitUsd: '0', hardCutoff: false, notes: '' });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadOrgsAndStatus = useCallback(async () => {
    setLoadingOrgs(true);
    setError(null);
    try {
      const client = getApiClient();
      const orgsResp = await client.get<{ success: boolean; data: { organizations: OrganizationListItem[] } }>(
        '/organizations'
      );
      const orgs = orgsResp.data.data?.organizations || [];

      const initialRows: OrgRowState[] = orgs.map((org) => ({
        org,
        status: null,
        loading: true,
      }));
      setRows(initialRows);

      const updated = await Promise.all(
        initialRows.map(async (row) => {
          try {
            const statusResp = await client.get<{ success: boolean; data: OrgBudgetStatus }>(
              `/llm-billing/budget/${row.org.id}`
            );
            return { ...row, status: statusResp.data.data, loading: false };
          } catch {
            return { ...row, status: null, loading: false };
          }
        })
      );
      setRows(updated);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to load organizations');
    } finally {
      setLoadingOrgs(false);
    }
  }, []);

  useEffect(() => {
    loadOrgsAndStatus();
  }, [loadOrgsAndStatus]);

  const startEdit = (orgId: string) => {
    const row = rows.find((r) => r.org.id === orgId);
    setEditing(orgId);
    setDraft({
      monthlyLimitUsd: row?.status?.budget?.monthlyLimitUsd
        ? row.status.budget.monthlyLimitUsd.toString()
        : '0',
      hardCutoff: row?.status?.budget?.hardCutoff || false,
      notes: row?.status?.budget?.notes || '',
    });
  };

  const cancelEdit = () => {
    setEditing(null);
    setError(null);
  };

  const saveEdit = async (orgId: string) => {
    setSaving(true);
    setError(null);
    try {
      const client = getApiClient();
      const monthlyLimitUsd = parseFloat(draft.monthlyLimitUsd);
      if (!Number.isFinite(monthlyLimitUsd) || monthlyLimitUsd < 0) {
        throw new Error('Monthly limit must be a non-negative number');
      }
      await client.put(`/llm-billing/budget/${orgId}`, {
        monthlyLimitUsd,
        hardCutoff: draft.hardCutoff,
        notes: draft.notes || undefined,
      });
      setEditing(null);
      await loadOrgsAndStatus();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="h-full overflow-y-auto p-6 space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-semibold">Org LLM Budgets</h2>
        <button
          onClick={loadOrgsAndStatus}
          className="px-3 py-1.5 text-sm border rounded hover:bg-muted"
          disabled={loadingOrgs}
        >
          Refresh
        </button>
      </div>

      {error && (
        <div className="border border-red-500 bg-red-500/10 rounded p-3 text-sm text-red-600">
          {error}
        </div>
      )}

      <div className="border rounded-lg overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-muted/40">
            <tr>
              <th className="text-left py-2 px-3">Organization</th>
              <th className="text-right py-2 px-3">Used / Limit</th>
              <th className="text-left py-2 px-3 w-[200px]">Progress</th>
              <th className="text-center py-2 px-3">Hard Cutoff</th>
              <th className="text-left py-2 px-3">Period Ends</th>
              <th className="text-right py-2 px-3">Actions</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => {
              const isEditing = editing === row.org.id;
              const status = row.status?.status;
              const limit = status?.limitUsd ?? 0;
              const used = status?.currentUsageUsd ?? 0;
              const pct = limit > 0 ? Math.min(1, used / limit) : 0;

              return (
                <React.Fragment key={row.org.id}>
                  <tr className="border-t border-border/60">
                    <td className="py-2 px-3 font-medium">{row.org.name}</td>
                    <td className="text-right py-2 px-3 tabular-nums">
                      {row.loading ? '…' : `${formatCost(used)} / ${formatCost(limit)}`}
                    </td>
                    <td className="py-2 px-3">
                      <div className="h-2 w-full bg-muted rounded overflow-hidden">
                        <div
                          className={`h-full ${progressColor(pct)}`}
                          style={{ width: `${Math.round(pct * 100)}%` }}
                        />
                      </div>
                    </td>
                    <td className="text-center py-2 px-3">
                      {row.status?.budget?.hardCutoff ? (
                        <span className="px-2 py-0.5 rounded text-xs bg-red-500/10 text-red-600">on</span>
                      ) : (
                        <span className="px-2 py-0.5 rounded text-xs bg-muted text-muted-foreground">off</span>
                      )}
                    </td>
                    <td className="py-2 px-3 text-muted-foreground">
                      {status?.periodEndsAt ? new Date(status.periodEndsAt).toLocaleDateString() : '—'}
                    </td>
                    <td className="text-right py-2 px-3">
                      {!isEditing && (
                        <button
                          onClick={() => startEdit(row.org.id)}
                          className="px-2 py-1 text-xs border rounded hover:bg-muted"
                        >
                          Edit
                        </button>
                      )}
                    </td>
                  </tr>
                  {isEditing && (
                    <tr className="border-t border-border/60 bg-muted/20">
                      <td colSpan={6} className="py-3 px-3">
                        <div className="flex flex-wrap items-end gap-3">
                          <label className="text-xs">
                            <div className="text-muted-foreground mb-1">Monthly limit (USD)</div>
                            <input
                              type="number"
                              min="0"
                              step="0.01"
                              value={draft.monthlyLimitUsd}
                              onChange={(e) =>
                                setDraft({ ...draft, monthlyLimitUsd: e.target.value })
                              }
                              className="border rounded px-2 py-1 text-sm w-32 bg-background"
                            />
                          </label>
                          <label className="text-xs flex items-center gap-2 mb-1">
                            <input
                              type="checkbox"
                              checked={draft.hardCutoff}
                              onChange={(e) =>
                                setDraft({ ...draft, hardCutoff: e.target.checked })
                              }
                            />
                            <span>Hard cutoff (refuse over budget)</span>
                          </label>
                          <label className="text-xs flex-1 min-w-[200px]">
                            <div className="text-muted-foreground mb-1">Notes</div>
                            <input
                              type="text"
                              value={draft.notes}
                              onChange={(e) => setDraft({ ...draft, notes: e.target.value })}
                              className="border rounded px-2 py-1 text-sm w-full bg-background"
                            />
                          </label>
                          <button
                            onClick={() => saveEdit(row.org.id)}
                            disabled={saving}
                            className="px-3 py-1.5 text-xs bg-primary text-primary-foreground rounded hover:opacity-90 disabled:opacity-50"
                          >
                            {saving ? 'Saving…' : 'Save'}
                          </button>
                          <button
                            onClick={cancelEdit}
                            disabled={saving}
                            className="px-3 py-1.5 text-xs border rounded hover:bg-muted"
                          >
                            Cancel
                          </button>
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
            {!loadingOrgs && rows.length === 0 && (
              <tr>
                <td colSpan={6} className="py-6 text-center text-muted-foreground">
                  No organizations found.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default OrgBudgetSettings;
